import { useCallback, useEffect, useState } from 'react';

/**
 * Vollbild: nur noch das Bild.
 *
 * Zwei Wege, und beide werden gegangen. Am großen Schirm gibt es die
 * Vollbild-Schnittstelle des Browsers -- die räumt auch die Adresszeile und
 * die Leisten des Betriebssystems weg. Auf dem iPhone gibt es sie nicht:
 * Safari kennt `requestFullscreen` nur am Video-Element, und unser Bild ist
 * eine Leinwand. Deshalb schaltet dieselbe Taste immer *auch* unseren eigenen
 * Zustand um -- die App räumt ihre eigenen Leisten weg. Auf dem Telefon ist
 * das alles, was geht, und es ist fast alles, was man will.
 *
 * Wer über die Taste des Browsers (Esc, oder die grüne Ampel am Mac) aus dem
 * echten Vollbild geht, soll nicht in unserem hängenbleiben: darauf hört der
 * Zuhörer unten.
 */

interface VollbildDoc extends Document {
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => unknown;
}
interface VollbildEl extends HTMLElement {
  webkitRequestFullscreen?: () => unknown;
}

function echtesVollbild(): boolean {
  const d = document as VollbildDoc;
  return Boolean(d.fullscreenElement ?? d.webkitFullscreenElement ?? null);
}

/** Kennt der Browser die Schnittstelle überhaupt? Auf dem iPhone: nein. */
export function kanntEchtesVollbild(): boolean {
  const el = document.documentElement as VollbildEl;
  return typeof el.requestFullscreen === 'function' || typeof el.webkitRequestFullscreen === 'function';
}

/** Liegt die App auf dem Home-Bildschirm? Dann gibt es ohnehin keine Adresszeile. */
export function alsApp(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)').matches === true ||
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

/**
 * Bleibt trotz Vollbild die Leiste des Browsers stehen?
 *
 * Auf dem iPhone kennt Safari `requestFullscreen` nur am Video-Element, und
 * unser Bild ist eine Leinwand -- die Adresszeile bleibt also, egal was wir
 * tun (Sascha, 7. September: „Vollbild macht kein Browser Vollbild“). Der
 * einzige Weg ohne sie führt über den Home-Bildschirm, und dort ist es dann
 * wirklich alles.
 */
export function leisteBleibt(): boolean {
  return !kanntEchtesVollbild() && !alsApp();
}

export function useVollbild(): { an: boolean; umschalten: () => void } {
  const [an, setAn] = useState(false);

  useEffect(() => {
    const folge = (): void => {
      // Nur der Weg hinaus wird übernommen. Hinein kommt man hier ohnehin nur
      // über unsere eigene Taste, und die hat den Zustand schon gesetzt.
      if (!echtesVollbild()) setAn(false);
    };
    document.addEventListener('fullscreenchange', folge);
    document.addEventListener('webkitfullscreenchange', folge);
    return () => {
      document.removeEventListener('fullscreenchange', folge);
      document.removeEventListener('webkitfullscreenchange', folge);
    };
  }, []);

  // Esc bringt auf dem Telefon heraus -- dort gibt es kein echtes Vollbild,
  // das der Browser für uns verlassen könnte.
  useEffect(() => {
    if (!an) return undefined;
    const taste = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setAn(false);
    };
    window.addEventListener('keydown', taste);
    return () => window.removeEventListener('keydown', taste);
  }, [an]);

  const umschalten = useCallback(() => {
    const hinein = !an;
    setAn(hinein);
    const el = document.documentElement as VollbildEl;
    const d = document as VollbildDoc;
    // Ein Fehlschlag ist kein Fehler: unser eigenes Vollbild steht auch ohne
    // die Schnittstelle, und genau das ist der Fall auf dem iPhone.
    try {
      if (hinein) void Promise.resolve(el.requestFullscreen?.() ?? el.webkitRequestFullscreen?.()).catch(() => undefined);
      else if (echtesVollbild()) void Promise.resolve(d.exitFullscreen?.() ?? d.webkitExitFullscreen?.()).catch(() => undefined);
    } catch {
      /* siehe oben */
    }
  }, [an]);

  return { an, umschalten };
}
